// =============================================
// HistoryTab — "Historia": ostatnie 100 wpisow dns_change_log, filtr po
// domenie, badge akcji + statusu, konto CF (label po cf_account_id),
// blad/powod, rozwijany before/after (JSON).
// =============================================
import { Fragment, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, RefreshCw, Search, ChevronDown, ChevronRight } from "lucide-react";
import type { CloudflareAccount } from "@/hooks/useCloudflareAccounts";
import { useDnsChangeLog } from "@/hooks/useDnsChangeLog";
import { DNS_ACTION_LABEL } from "@/services/cloudflare/dnsChangeLogService";
import { StatusBadge } from "@/components/cloudflare/shared/StatusBadge";

const fmt = (iso: string) => new Date(iso).toLocaleString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
const pretty = (v: unknown) => (v == null ? "—" : JSON.stringify(v, null, 2));

export const HistoryTab = ({ accounts }: { accounts: CloudflareAccount[] }) => {
  const { rows, loading, error, domainFilter, setDomainFilter, refresh } = useDnsChangeLog(100);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const accLabel = (id: string | null) => (id ? accounts.find((a) => a.id === id)?.label ?? "(usunięte)" : "—");

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={domainFilter} onChange={(e) => setDomainFilter(e.target.value)} placeholder="Filtruj po domenie…" className="pl-8 w-64" />
        </div>
        <Button variant="outline" onClick={() => void refresh()} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}Odśwież
        </Button>
        <span className="text-xs text-muted-foreground">ostatnie 100 wpisów{domainFilter ? ` · filtr „${domainFilter}”` : ""}</span>
      </div>

      {error ? (
        <Card><CardContent className="py-8 text-center text-sm text-red-400">{error}{/relation .* does not exist/i.test(error) ? " — tabela dns_change_log nie jest jeszcze zaaplikowana (migracja 20261003_dns_change_log.sql)." : ""}</CardContent></Card>
      ) : rows.length === 0 && !loading ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground text-sm">Brak wpisów. Historia wypełnia się po akcjach kreatora „Przenieś domeny” i akcjach w „Domeny w CF”.</CardContent></Card>
      ) : (
        <div className="rounded-lg border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>Data</TableHead>
                <TableHead>Domena</TableHead>
                <TableHead>Akcja</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Konto CF</TableHead>
                <TableHead>Błąd / powód</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const isOpen = !!open[r.id];
                return (
                  <Fragment key={r.id}>
                    <TableRow className="cursor-pointer" onClick={() => setOpen((p) => ({ ...p, [r.id]: !isOpen }))}>
                      <TableCell className="px-2">{isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{fmt(r.created_at)}</TableCell>
                      <TableCell className="font-mono text-sm">
                        {r.domain}
                        {r.workspace === "sideprojects" && <span className="ml-1.5 text-xs" title="Sponsoraki">🎨</span>}
                      </TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{DNS_ACTION_LABEL[r.action] ?? r.action}</Badge></TableCell>
                      <TableCell><StatusBadge status={r.status} /></TableCell>
                      <TableCell className="text-xs">{accLabel(r.cf_account_id)}</TableCell>
                      <TableCell className="text-xs max-w-[360px] truncate" title={r.error ?? ""}>{r.error ?? ""}</TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow>
                        <TableCell colSpan={7} className="bg-muted/20">
                          <div className="grid gap-3 md:grid-cols-2 text-xs">
                            <div>
                              <div className="text-muted-foreground mb-1">before</div>
                              <pre className="rounded bg-background/60 p-2 overflow-x-auto max-h-48 font-mono">{pretty(r.before)}</pre>
                            </div>
                            <div>
                              <div className="text-muted-foreground mb-1">after</div>
                              <pre className="rounded bg-background/60 p-2 overflow-x-auto max-h-48 font-mono">{pretty(r.after)}</pre>
                            </div>
                          </div>
                          {r.error && <p className="mt-2 text-xs break-words">{r.error}</p>}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
